/**
 * Request Demo dialog. Calls POST /api/public/demo-requests (anonymous).
 */
import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export function RequestDemoDialog({ open, onOpenChange }) {
    const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const reset = () => { setForm({ name: "", email: "", company: "", phone: "", message: "" }); setDone(false); };

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required"); return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${BASE}/api/public/demo-requests`, form);
            setDone(true);
            toast.success("Thanks — we'll reach out shortly.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit request");
        } finally { setSubmitting(false); }
    };

    const close = () => { onOpenChange(false); setTimeout(reset, 200); };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
            <DialogContent className="rounded-sm max-w-lg" data-testid="request-demo-dialog">
                <DialogHeader>
                    <DialogTitle>Request a demo</DialogTitle>
                    <DialogDescription>
                        Tell us about your use case — our team will get in touch within one business day.
                    </DialogDescription>
                </DialogHeader>

                {done ? (
                    <div className="py-8 text-center" data-testid="demo-thanks">
                        <div className="text-5xl mb-4">🙏</div>
                        <h3 className="text-xl font-bold mb-2">Request received</h3>
                        <p className="text-sm text-muted-foreground">We'll email <strong>{form.email}</strong> shortly.</p>
                        <Button onClick={close} className="mt-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="demo-close-btn">Close</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Name *</Label>
                                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" data-testid="demo-name-input" />
                            </div>
                            <div>
                                <Label className="label-overline">Work email *</Label>
                                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-sm" data-testid="demo-email-input" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Company</Label>
                                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-2 rounded-sm" data-testid="demo-company-input" />
                            </div>
                            <div>
                                <Label className="label-overline">Phone</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-sm" data-testid="demo-phone-input" />
                            </div>
                        </div>
                        <div>
                            <Label className="label-overline">What would you like to build?</Label>
                            <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="e.g. internal HR portal with approvals" className="mt-2 rounded-sm" data-testid="demo-message-input" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={close} className="rounded-sm">Cancel</Button>
                            <Button type="submit" disabled={submitting} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="demo-submit-btn">
                                {submitting ? "Sending…" : "Request demo"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
